import { useEffect } from "react";
import { useSEO } from "@/hooks/useSEO";

export default function PrivacyPolicy() {
  useSEO({
    title: "Privacy Policy | Precision Pavers & Pool Design",
    description:
      "Privacy policy for Precision Pavers & Pool Design. Learn how we collect, use, and protect your personal information when you request a free estimate or visit our website.",
    canonicalUrl: "https://precisionpaverspools.com/privacy-policy",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://precisionpaverspools.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Privacy Policy",
          item: "https://precisionpaverspools.com/privacy-policy",
        },
      ],
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://vibe.filesafe.space/1779750798598342338/attachments/1043eb3f-4992-4ddb-a308-b1e54e60191e.jpg"
            alt="Privacy Policy Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="container relative z-10 mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white tracking-tight drop-shadow-md mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-white/80">
            Effective Date: August 1, 2023
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl text-muted-foreground leading-relaxed">
          <p>
            Precision Pavers & Pool Design ("us," "we," or "our") is committed
            to protecting your privacy. This Privacy Policy outlines our
            practices regarding the collection, use, and disclosure of personal
            information when you use our website and services.
          </p>

          <h2 className="text-xl font-semibold mt-10 mb-3 text-foreground">
            Information Collection and Use
          </h2>
          <p>
            We collect personal information that you provide directly to us,
            such as your name, contact details, and project preferences. This
            information is used to respond to your inquiries, provide services,
            and improve user experience.
          </p>

          <h2 className="text-xl font-semibold mt-10 mb-3 text-foreground">
            Log Data
          </h2>
          <p>
            We may collect information that your browser sends whenever you
            visit our website ("Log Data"). This Log Data may include
            information such as your computer's Internet Protocol ("IP")
            address, browser type, browser version, the pages of our website
            that you visit, the time and date of your visit, the time spent on
            those pages, and other statistics.
          </p>

          <h2 className="text-xl font-semibold mt-10 mb-3 text-foreground">
            Cookies
          </h2>
          <p>
            We use "cookies" to collect information and improve our services.
            You have the option to disable cookies through your browser
            settings; however, this may affect certain functionalities of our
            website.
          </p>

          <h2 className="text-xl font-semibold mt-10 mb-3 text-foreground">
            Service Providers
          </h2>
          <p>
            We may employ third-party companies and individuals to facilitate
            our services, to provide services on our behalf, and to assist us in
            analyzing how our services are used.
          </p>

          <h2 className="text-xl font-semibold mt-10 mb-3 text-foreground">
            Security
          </h2>
          <p>
            We prioritize the security of your personal information. However,
            please be aware that no method of transmission over the internet or
            electronic storage is completely secure.
          </p>

          <h2 className="text-xl font-semibold mt-10 mb-3 text-foreground">
            Links to Other Sites
          </h2>
          <p>
            Our website may contain links to other sites. We are not responsible
            for the content or privacy practices of those sites and encourage
            you to review their respective privacy policies.
          </p>

          <h2 className="text-xl font-semibold mt-10 mb-3 text-foreground">
            Changes to This Privacy Policy
          </h2>
          <p>
            We may update our Privacy Policy from time to time. You are advised
            to review this page periodically for any changes. Changes will be
            effective upon posting on this page.
          </p>

          <h2 className="text-xl font-semibold mt-10 mb-3 text-foreground">
            Contact Us
          </h2>
          <p>
            If you have any questions about our Privacy Policy, please contact
            us at{" "}
            <a
              href="mailto:info@precisionpaverspools.com"
              className="text-secondary hover:underline"
            >
              info@precisionpaverspools.com
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
